import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { fileApi, FileUpload as FileUploadType } from '../services/api';
import { getUserFriendlyErrorMessage } from '../utils/errorUtils';
import { ColumnSelectionInfo, ColumnEditCallbacks } from '../types/columnSelection';
import './FileManager.css';

interface FileManagerProps extends ColumnEditCallbacks {
  sessionId: string;
  onFilesChanged: (files: FileUploadType[]) => void;
  externalFiles?: FileUploadType[];
}

interface UploadedFile extends FileUploadType {
  preview?: any;
  loading?: boolean;
  error?: string;
  status?: 'uploading' | 'ready' | 'error';
}

const FileManager: React.FC<FileManagerProps> = ({ sessionId, onFilesChanged, externalFiles = [], onEditColumns, hasColumnSelection, columnSelectionInfo }) => {
  const [internalFiles, setInternalFiles] = useState<UploadedFile[]>([]);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<File[]>([]);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});

  // State for tracking animating files
  const [animatingFiles, setAnimatingFiles] = useState<Set<string>>(new Set());
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Refs for horizontal scrolling
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  // Check scroll availability
  const checkScrollButtons = useCallback(() => {
    if (!tabsContainerRef.current || !tabsRef.current) return;
    
    const container = tabsContainerRef.current;
    const tabs = tabsRef.current;
    
    const containerWidth = container.offsetWidth;
    const tabsWidth = tabs.scrollWidth;
    
    setCanScrollLeft(scrollOffset > 0);
    setCanScrollRight(scrollOffset < tabsWidth - containerWidth);
  }, [scrollOffset]);

  // Update scroll buttons when files change or window resizes
  useEffect(() => {
    checkScrollButtons();
    
    const handleResize = () => checkScrollButtons();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, [internalFiles, scrollOffset, checkScrollButtons]);

  // Scroll functions
  const scrollTabs = useCallback((direction: 'left' | 'right') => {
    if (!tabsContainerRef.current || !tabsRef.current) return;
    
    const container = tabsContainerRef.current;
    const scrollAmount = container.offsetWidth * 0.8; // Scroll 80% of container width
    
    const newOffset = direction === 'left' 
      ? Math.max(0, scrollOffset - scrollAmount)
      : Math.min(tabsRef.current.scrollWidth - container.offsetWidth, scrollOffset + scrollAmount);
    
    setScrollOffset(newOffset);
    tabsRef.current.style.transform = `translateX(-${newOffset}px)`;
  }, [scrollOffset]);

  // Sync external files with internal state
  useEffect(() => {
    if (externalFiles.length > 0) {
      setInternalFiles(prev => {
        // Do all logic inside setState to avoid dependency issues
        const existingIds = new Set(prev.map(f => f.file_id));
        // Filter to only show user uploads, not workflow outputs
        const userUploadFiles = externalFiles.filter(ext => 
          !ext.file_source || ext.file_source === 'user_upload'
        );
        const newFiles = userUploadFiles.filter(ext => !existingIds.has(ext.file_id));
        
        if (newFiles.length > 0) {
          // Track files for animation
          const newFileIds = newFiles.map(f => f.file_id);
          setAnimatingFiles(new Set(newFileIds));
          
          // Clear previous timeout
          if (animationTimeoutRef.current) {
            clearTimeout(animationTimeoutRef.current);
          }
          
          // Clear animation after delay
          animationTimeoutRef.current = setTimeout(() => {
            setAnimatingFiles(new Set());
          }, 1000);
          
          // Auto-select first new file if no active tab
          if (!activeTab) {
            setActiveTab(newFiles[0].file_id);
          }
          
          // Fetch previews for external files
          newFiles.forEach(async (file) => {
            try {
              const previewResponse = await fileApi.preview(file.file_id);
              setInternalFiles(current => current.map(f => 
                f.file_id === file.file_id 
                  ? { ...f, preview: previewResponse.data, loading: false, status: 'ready' as const }
                  : f
              ));
            } catch (error) {
              console.error('Preview error for external file:', error);
              setInternalFiles(current => current.map(f => 
                f.file_id === file.file_id 
                  ? { ...f, loading: false, error: 'Failed to generate preview', status: 'error' as const }
                  : f
              ));
            }
          });
          
          // Return files with loading state
          return [...prev, ...newFiles.map(f => ({ ...f, loading: true, status: 'uploading' as const }))];
        }
        return prev;
      });
    }
  }, [externalFiles]); // Removed internalFiles dependency!

  // Combined files for display
  const uploadedFiles = internalFiles;

  const getFileIcon = (fileType: string) => {
    if (fileType === 'csv' || fileType === 'xlsx') {
      return '📊';
    } else if (fileType === 'pdf' || fileType === 'docx') {
      return '📄';
    }
    return '📎';
  };

  const handleFileUpload = async (file: File) => {
    // Validate file size (empty files)
    if (file.size === 0) {
      console.error('Cannot upload empty file');
      alert('Cannot upload empty file. Please select a file with content.');
      return;
    }
    
    // Validate file size (max 100MB)
    const maxSizeInBytes = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSizeInBytes) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      console.error(`File too large: ${sizeMB}MB`);
      alert(`File size (${sizeMB}MB) exceeds the maximum allowed size of 100MB.`);
      return;
    }
    
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const current = prev[tempId] || 0;
          if (current >= 90) {
            clearInterval(progressInterval);
            return { ...prev, [tempId]: 90 };
          }
          return { ...prev, [tempId]: current + 10 };
        });
      }, 200);

      // Upload file
      const uploadResponse = await fileApi.upload(file, sessionId);
      clearInterval(progressInterval);
      setUploadProgress(prev => ({ ...prev, [tempId]: 100 }));

      const newFile: UploadedFile = {
        ...uploadResponse.data,
        loading: true,
        status: 'uploading' as const
      };

      // Add to uploaded files
      setInternalFiles(prev => {
        const updated = [...prev, newFile];
        onFilesChanged(updated);
        return updated;
      });

      // Set as active tab if it's the first file or no tab is active
      if (!activeTab || uploadedFiles.length === 0) {
        setActiveTab(newFile.file_id);
      }

      // Get preview
      try {
        const previewResponse = await fileApi.preview(uploadResponse.data.file_id);
        setInternalFiles(prev => prev.map(f => 
          f.file_id === newFile.file_id 
            ? { ...f, preview: previewResponse.data, loading: false, status: 'ready' as const }
            : f
        ));
      } catch (error) {
        console.error('Preview error:', error);
        setInternalFiles(prev => prev.map(f => 
          f.file_id === newFile.file_id 
            ? { ...f, loading: false, error: 'Failed to generate preview', status: 'error' as const }
            : f
        ));
      }

      // Clean up progress
      setTimeout(() => {
        setUploadProgress(prev => {
          const { [tempId]: _, ...rest } = prev;
          return rest;
        });
      }, 500);

    } catch (err: any) {
      console.error('Upload error:', err);
      setUploadProgress(prev => {
        const { [tempId]: _, ...rest } = prev;
        return rest;
      });
      throw err;
    }
  };

  const processUploadQueue = useCallback(async () => {
    if (uploadQueue.length === 0 || uploading) return;

    setUploading(true);
    const errors: string[] = [];

    for (let i = 0; i < uploadQueue.length; i++) {
      setCurrentUploadIndex(i);
      try {
        await handleFileUpload(uploadQueue[i]);
      } catch (err: any) {
        errors.push(`${uploadQueue[i].name}: ${getUserFriendlyErrorMessage(err)}`);
      }
    }

    setUploadQueue([]);
    setCurrentUploadIndex(0);
    setUploading(false);

    if (errors.length > 0) {
      alert(`Some files failed to upload:\n${errors.join('\n')}`);
    }
  }, [uploadQueue, uploading, sessionId, handleFileUpload]);

  React.useEffect(() => {
    processUploadQueue();
  }, [processUploadQueue]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    // Check if we would exceed 10 files
    if (uploadedFiles.length + acceptedFiles.length > 10) {
      alert('Maximum 10 files allowed. Please remove some files first.');
      return;
    }

    setUploadQueue(acceptedFiles);
  }, [uploadedFiles.length]);

  const removeFile = (fileId: string) => {
    setInternalFiles(prev => {
      const updated = prev.filter(f => f.file_id !== fileId);
      onFilesChanged(updated);
      
      // If we removed the active tab, select another
      if (activeTab === fileId && updated.length > 0) {
        setActiveTab(updated[0].file_id);
      } else if (updated.length === 0) {
        setActiveTab(null);
        // Reset scroll when no files
        setScrollOffset(0);
        if (tabsRef.current) {
          tabsRef.current.style.transform = 'translateX(0)';
        }
      }
      
      return updated;
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxSize: 100 * 1024 * 1024, // 100MB
    disabled: uploading,
    onDropRejected: (rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors[0]?.code === 'file-too-large') {
          const sizeMB = (rejection.file.size / (1024 * 1024)).toFixed(2);
          alert(`File size (${sizeMB}MB) exceeds the maximum allowed size of 100MB.`);
        } else if (rejection.errors[0]?.code === 'file-invalid-type') {
          alert('Invalid file type. Please upload CSV, Excel, PDF, or Word documents.');
        } else {
          alert('File upload failed. Please try again.');
        }
      }
    },
  });

  const activeFile = uploadedFiles.find(f => f.file_id === activeTab);

  return (
    <div className="file-manager-container">
      <div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'disabled' : ''} ${uploadedFiles.length > 0 ? 'compact' : ''}`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className="upload-status">
            <div className="spinner"></div>
            <p>Uploading file {currentUploadIndex + 1} of {uploadQueue.length}...</p>
            {Object.entries(uploadProgress).map(([id, progress]) => (
              <div key={id} className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                />
              </div>
            ))}
          </div>
        ) : isDragActive ? (
          <p>Drop files here...</p>
        ) : (
          <div className="upload-prompt">
            <p>{uploadedFiles.length > 0 ? 'Drop more files or click to browse' : 'Drag & drop files here'}</p>
            <p className="sub-text">Supports CSV, Excel, PDF, and Word documents (max 10 files)</p>
          </div>
        )}
      </div>

      {uploadedFiles.length > 0 && (
        <>
          <div className="file-tabs-container">
            {canScrollLeft && (
              <button 
                className="file-tabs-scroll-btn left"
                onClick={() => scrollTabs('left')}
                aria-label="Scroll left"
              >
                ‹
              </button>
            )}
            
            <div className="file-tabs-scroll-wrapper" ref={tabsContainerRef}>
              <div className="file-tabs" ref={tabsRef}>
                {uploadedFiles.map(file => (
                  <div
                    key={file.file_id}
                    data-file-id={file.file_id}
                    className={`file-tab ${activeTab === file.file_id ? 'active' : ''} ${animatingFiles.has(file.file_id) ? 'file-added' : ''} ${file.status ? `status-${file.status}` : ''}`}
                    onClick={() => setActiveTab(file.file_id)}
                  >
                    <span className="file-icon">
                      {file.status === 'error' ? '⚠️' : getFileIcon(file.file_type)}
                    </span>
                    <span className="file-name">{file.filename}</span>
                    {file.status === 'uploading' && <span className="file-status">↑</span>}
                    {file.status === 'error' && file.error && (
                      <span className="file-error-tooltip" title={file.error}>!</span>
                    )}
                    <button
                      className="remove-file"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(file.file_id);
                      }}
                      title="Remove file"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            {canScrollRight && (
              <button 
                className="file-tabs-scroll-btn right"
                onClick={() => scrollTabs('right')}
                aria-label="Scroll right"
              >
                ›
              </button>
            )}
          </div>

          {activeFile && (
            <div className="file-preview-container">
              {activeFile.loading ? (
                <div className="preview-loading">
                  <div className="spinner small"></div>
                  <p>Processing file preview...</p>
                </div>
              ) : activeFile.error ? (
                <div className="preview-error">
                  <p>{activeFile.error}</p>
                </div>
              ) : activeFile.preview ? (
                <FilePreview 
                  file={activeFile} 
                  preview={activeFile.preview} 
                  onEditColumns={onEditColumns}
                  hasColumnSelection={hasColumnSelection}
                  columnSelectionInfo={columnSelectionInfo}
                />
              ) : null}
            </div>
          )}
        </>
      )}

      <div className="file-summary">
        <p>{uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} uploaded</p>
      </div>
    </div>
  );
};

// Separate component for file previews
interface FilePreviewProps extends ColumnEditCallbacks {
  file: UploadedFile;
  preview: any;
}

const FilePreview: React.FC<FilePreviewProps> = ({ file, preview, onEditColumns, hasColumnSelection, columnSelectionInfo }) => {
  const isDocument = file.file_type === 'pdf' || file.file_type === 'docx';

  if (isDocument) {
    // Document preview (PDF/DOCX)
    return (
      <div className="document-preview">
        <div className="preview-header">
          <h3>{file.filename}</h3>
          <div className="preview-info">
            <p><strong>Type:</strong> {file.file_type.toUpperCase()}</p>
            <p><strong>Size:</strong> {(file.size_bytes / 1024).toFixed(2)} KB</p>
          </div>
        </div>
        <div className="document-content">
          <pre>{preview.sample_data[0]?.content || 'No content available'}</pre>
        </div>
      </div>
    );
  } else {
    // Tabular preview (CSV/Excel)
    return (
      <div className="tabular-preview">
        <div className="preview-header">
          <div className="header-with-action">
            <h3>{file.filename}</h3>
            {onEditColumns && (
              <button 
                className={`btn-edit-columns ${columnSelectionInfo?.hasBeenModified ? 'modified' : ''}`}
                onClick={onEditColumns}
                title={hasColumnSelection ? "Edit column selection" : "Select columns for workflow"}
                disabled={!onEditColumns}
              >
                <span style={{ fontSize: '1.1em' }}>✏️</span>
                Edit Columns
                {columnSelectionInfo && columnSelectionInfo.selectedColumns > 0 && (
                  <span className="column-count">
                    • {columnSelectionInfo.selectedColumns} of {columnSelectionInfo.totalColumns} selected
                    {columnSelectionInfo.hasBeenModified && (
                      <span className="modified-indicator" title="Column selection has been modified"> (modified)</span>
                    )}
                  </span>
                )}
              </button>
            )}
          </div>
          <div className="preview-info">
            <p><strong>Rows:</strong> {preview.total_rows}</p>
            <p><strong>Columns:</strong> {preview.total_columns}</p>
          </div>
        </div>
        {preview.sample_data.length > 0 && (
          <div className="preview-data">
            <strong>Preview Data:</strong>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    {preview.columns.map((col: string, index: number) => (
                      <th key={index}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sample_data.map((row: any, rowIndex: number) => (
                    <tr key={rowIndex}>
                      {preview.columns.map((col: string, colIndex: number) => (
                        <td key={colIndex}>{row[col]?.toString() || ''}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }
};

export default FileManager;